import { Controller } from '@hotwired/stimulus'
import { createPopper } from '@popperjs/core'
import { fetchBotFunctions } from '../lib/bot_functions_cache'
import '../../assets/stylesheets/components/bot_function_autocomplete.css'

const MAX_FILTER_LENGTH = 50
const EDITOR_RETRY_INTERVAL = 200
const EDITOR_MAX_RETRIES = 10

export default class extends Controller {
  static values = {
    trigger: { type: String, default: '/' },
    // When true, offer only merchant-configured tools (custom functions + MCP tools), e.g. for
    // lifecycle hooks. Defaults to the full list including built-ins.
    merchantOnly: { type: Boolean, default: false }
  }

  get functionsUrl() {
    return this.merchantOnlyValue ? '/ai/bot_functions?merchant_only=true' : '/ai/bot_functions'
  }

  connect() {
    this.cache = null
    this.dropdown = null
    this.searchInput = null
    this.resultsContainer = null
    this.popperInstance = null
    this.anchorOffset = null
    this.selectedIndex = 0
    this.showUnavailable = false
    this.selectableFunctions = []

    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleEditorScroll = this.handleEditorScroll.bind(this)

    this.fetchFunctions()
    setTimeout(() => this.setupListener(), 100)
  }

  disconnect() {
    this.destroyDropdown()
    if (this.editorElement) {
      this.editorElement.removeEventListener('keydown', this.handleKeydown)
      this.editorElement.removeEventListener('scroll', this.handleEditorScroll)
    }
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  // --- Editor setup ---

  setupListener(retries = 0) {
    this.editor = this.element.editorInstance
    if (!this.editor) {
      if (retries < EDITOR_MAX_RETRIES) setTimeout(() => this.setupListener(retries + 1), EDITOR_RETRY_INTERVAL)
      return
    }

    this.editorElement = this.element.nextElementSibling?.querySelector('.toastui-editor-contents[contenteditable="true"]')
    if (!this.editorElement) {
      if (retries < EDITOR_MAX_RETRIES) setTimeout(() => this.setupListener(retries + 1), EDITOR_RETRY_INTERVAL)
      return
    }

    this.handleKeydown = this.handleKeydown.bind(this)
    this.editorElement.addEventListener('keydown', this.handleKeydown, true)
  }

  async fetchFunctions() {
    try {
      this.cache = await fetchBotFunctions(this.functionsUrl)
    } catch (error) {
      console.error('Failed to fetch bot functions:', error)
      this.cache = []
    }
  }

  // --- Keyboard handling ---

  async handleKeydown(event) {
    if (event.key === this.triggerValue && !this.isDropdownVisible()) {
      event.preventDefault()
      if (!this.cache) await this.fetchFunctions()
      this.selectedIndex = 0
      this.showUnavailable = false
      this.showDropdown()
    }
  }

  handleSearchKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigate(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        this.navigate(-1)
        return
      case 'Enter':
      case 'Tab':
        event.preventDefault()
        this.selectCurrent()
        return
      case 'Escape':
        event.preventDefault()
        this.hideDropdown()
        return
      case 'Backspace':
        if (this.searchInput.value === '') {
          event.preventDefault()
          this.hideDropdown()
        }
        return
      default:
        if (event.key === this.triggerValue && this.searchInput.value === '') {
          event.preventDefault()
          this.hideDropdown()
          this.editor?.insertText(this.triggerValue)
        }
        return
    }
  }

  // --- Dropdown lifecycle ---

  showDropdown() {
    this.captureAnchorOffset()
    this.ensureDropdownElement()
    this.searchInput.value = ''
    this.dropdown.style.display = 'flex'
    this.renderItems()
    this.createPopper()
    this.searchInput.focus()
    document.addEventListener('mousedown', this.handleClickOutside)
    this.editorElement?.addEventListener('scroll', this.handleEditorScroll)
  }

  hideDropdown() {
    this.destroyPopper()
    if (this.dropdown) this.dropdown.style.display = 'none'
    this.selectedIndex = 0
    this.editorElement?.focus()
    document.removeEventListener('mousedown', this.handleClickOutside)
    this.editorElement?.removeEventListener('scroll', this.handleEditorScroll)
  }

  destroyDropdown() {
    this.destroyPopper()
    if (this.dropdown) {
      this.dropdown.remove()
      this.dropdown = null
      this.searchInput = null
      this.resultsContainer = null
    }
  }

  isDropdownVisible() {
    return this.dropdown && this.dropdown.style.display !== 'none'
  }

  ensureDropdownElement() {
    if (this.dropdown) return

    this.dropdown = document.createElement('div')
    this.dropdown.className = 'bot-function-autocomplete'

    // Search bar
    const searchBar = document.createElement('div')
    searchBar.className = 'bot-function-autocomplete__search'

    searchBar.innerHTML = `<svg class="bot-function-autocomplete__search-icon" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/>
    </svg>`

    this.searchInput = document.createElement('input')
    this.searchInput.className = 'bot-function-autocomplete__search-input'
    this.searchInput.type = 'text'
    this.searchInput.placeholder = 'Filter actions...'
    this.searchInput.setAttribute('autocomplete', 'off')
    this.searchInput.maxLength = MAX_FILTER_LENGTH

    this.searchInput.addEventListener('input', () => {
      this.selectedIndex = 0
      this.renderItems()
    })

    this.searchInput.addEventListener('keydown', (e) => this.handleSearchKeydown(e))

    searchBar.appendChild(this.searchInput)
    this.dropdown.appendChild(searchBar)

    // Results container
    this.resultsContainer = document.createElement('div')
    this.resultsContainer.className = 'bot-function-autocomplete__results'
    this.resultsContainer.addEventListener('mousedown', (event) => {
      event.preventDefault()
      event.stopPropagation()
      const item = event.target.closest('.function-item')
      if (item) {
        this.selectedIndex = parseInt(item.dataset.index)
        this.selectCurrent()
      }
    })
    this.dropdown.appendChild(this.resultsContainer)

    document.body.appendChild(this.dropdown)
  }

  handleClickOutside(event) {
    if (this.isDropdownVisible() && !this.dropdown.contains(event.target)) {
      this.hideDropdown()
    }
  }

  // --- Popper positioning ---

  createPopper() {
    this.destroyPopper()
    const self = this
    const virtualElement = {
      getBoundingClientRect() {
        const editorRect = self.editorElement?.getBoundingClientRect()
        if (!editorRect || !self.anchorOffset) {
          return { top: 0, right: 0, bottom: 0, left: 0, width: 0, height: 0, x: 0, y: 0 }
        }
        const top = editorRect.top + self.anchorOffset.top - self.editorElement.scrollTop
        const left = editorRect.left + self.anchorOffset.left - self.editorElement.scrollLeft
        const height = self.anchorOffset.height
        return { top, right: left, bottom: top + height, left, width: 0, height, x: left, y: top }
      }
    }
    this.popperInstance = createPopper(virtualElement, this.dropdown, {
      placement: 'bottom-start',
      strategy: 'fixed',
      modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
        { name: 'preventOverflow', options: { padding: 16 } }
      ]
    })
  }

  destroyPopper() {
    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
  }

  captureAnchorOffset() {
    const cursorRect = this.getCursorViewportRect()
    const editorRect = this.editorElement?.getBoundingClientRect()
    if (cursorRect && editorRect) {
      this.anchorOffset = {
        top: cursorRect.top - editorRect.top + this.editorElement.scrollTop,
        left: cursorRect.left - editorRect.left + this.editorElement.scrollLeft,
        height: cursorRect.height
      }
    } else {
      this.anchorOffset = { top: 0, left: 0, height: 0 }
    }
  }

  getCursorViewportRect() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return null

    const range = selection.getRangeAt(0)
    let rect = range.getBoundingClientRect()

    if (rect.width === 0 && rect.height === 0 && range.collapsed) {
      const span = document.createElement('span')
      span.textContent = '\u200b'
      range.insertNode(span)
      rect = span.getBoundingClientRect()
      span.parentNode.removeChild(span)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    return (rect.top > 0 || rect.left > 0) ? rect : null
  }

  handleEditorScroll() {
    this.popperInstance?.update()
  }

  // --- Rendering ---

  renderItems() {
    if (!this.cache || !this.resultsContainer) return

    const filterText = this.searchInput?.value || ''
    const filtered = this.filterFunctions(filterText)

    if (filtered.length === 0) {
      this.resultsContainer.innerHTML = '<div class="function-item-empty">No matching actions</div>'
      return
    }

    const available = filtered.filter(f => f.available)
    const unavailable = filtered.filter(f => !f.available)
    const expanded = this.showUnavailable || filterText.length > 0
    this.selectableFunctions = expanded ? [...available, ...unavailable] : available

    let html = available.map((func, i) => this.renderItem(func, i)).join('')

    if (unavailable.length > 0) {
      if (expanded) {
        html += '<div class="bot-function-autocomplete__divider">Requires Setup</div>'
        html += unavailable.map((func, i) => this.renderItem(func, available.length + i)).join('')
      } else {
        html += `
          <div class="bot-function-autocomplete__toggle" data-action="show-unavailable">
            <span class="bot-function-autocomplete__toggle-icon">+</span>
            ${unavailable.length} more requiring setup
          </div>`
      }
    }

    if (available.length === 0 && !expanded) {
      html += '<div class="function-item-empty">No available actions match</div>'
    }

    this.resultsContainer.innerHTML = html

    const toggle = this.resultsContainer.querySelector('[data-action="show-unavailable"]')
    if (toggle) {
      toggle.addEventListener('mousedown', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.showUnavailable = true
        this.renderItems()
      })
    }
  }

  renderItem(func, index) {
    const selected = index === this.selectedIndex ? 'selected' : ''
    const statusClass = func.available ? 'function-item__status--available' : 'function-item__status--unavailable'
    const missing = !func.available && func.missing_requirements.length > 0
      ? `<span class="function-item__missing">${this.escapeHtml(func.missing_requirements.join(', '))}</span>`
      : ''

    return `
      <div class="function-item ${selected}" data-index="${index}" data-function-key="${func.key}">
        <span class="function-item__status ${statusClass}">${func.available ? '✓' : '!'}</span>
        <div class="function-item__body">
          <div class="function-item__header">
            <span class="function-item__name">${this.escapeHtml(func.display_name)}</span>
            ${func.category ? `<span class="function-item__category">${this.escapeHtml(func.category)}</span>` : ''}
          </div>
          ${missing}
        </div>
      </div>`
  }

  // --- Filtering & navigation ---

  filterFunctions(filterText) {
    if (!this.cache) return []
    if (filterText === '') return this.cache

    const tokens = filterText.toLowerCase().split(/\s+/).filter(Boolean)
    return this.cache.filter(f => {
      const searchable = `${f.key} ${f.display_name} ${f.category || ''}`.toLowerCase()
      return tokens.every(token => searchable.includes(token))
    })
  }

  navigate(direction) {
    const len = this.selectableFunctions?.length
    if (!len) return
    this.selectedIndex = (this.selectedIndex + direction + len) % len
    this.renderItems()
  }

  selectCurrent() {
    const func = this.selectableFunctions?.[this.selectedIndex]
    if (func) this.insertFunction(func)
    this.hideDropdown()
  }

  insertFunction(func) {
    if (!this.editor || !this.editorElement) return

    const wwEditor = this.editor.wwEditor || this.editor.getCurrentModeEditor?.()
    const view = wwEditor?.view
    const widgetType = view?.state.schema.nodes.widget

    if (view && widgetType) {
      const { tr, selection, schema } = view.state
      const widgetContent = `$$widget0 ${func.key}$$`
      const widgetNode = widgetType.create({ info: 'widget0' }, schema.text(widgetContent))
      const space = schema.text(' ')
      tr.replaceSelectionWith(widgetNode).insert(tr.selection.from, space)
      view.dispatch(tr)
    } else {
      this.editor.insertText(`${func.key} `)
    }

    this.editorElement.focus()
  }

  // --- Utilities ---

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
